<script setup lang="ts">
import TextInput from '@/components/TextInput.vue'
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import {silenceAbortError} from '@/services/abort/AbortControllerService'
import AbortServicesHandler from '@/services/abort/AbortServicesHandler'
import type {Company} from '@/services/CompanyService'
import companyApi, {CompanyService, IS_DIGIT_REGEX} from '@/services/CompanyService'
import {useCompanyStore} from '@/stores/company'
import {useGlobalStore} from '@/stores/global'
import CompanyPropertiesCookie from '@/utils/CompanyPropertiesCookie'
import delayWrapper from '@/utils/delayWrapper'
import {computed, onMounted, ref} from 'vue'
import {useI18n} from 'vue-i18n'

const ASCENDING = true
const FILTER_THRESHOLD = 15
const PAGE_SIZE = 15
const SORT_BY_KEY = 'name'

interface Props {
  light?: boolean
  scrollable?: boolean
}

const companyStore = useCompanyStore()
const globalStore = useGlobalStore()
const isNewUiEnabled = useNewUiEnabled()
const {t} = useI18n()

const companyService = new CompanyService()
const abortServicesHandler = new AbortServicesHandler(companyService)

const props = withDefaults(defineProps<Props>(), {
  light: true,
  scrollable: true,
})

const isFetchingData = ref(false)
const fetchedCompanies = ref<Company[]>()
const totalCompaniesAssigned = ref(0)
const hotelFilter = ref('')

const activeCompanies = computed(() => fetchedCompanies.value?.filter((c) => c.isActive) ?? [])
const hotelsWithoutCurrent = computed(() => activeCompanies.value.filter((c) => c.id !== companyStore.id))

const showFilter = computed(() => globalStore.hasAuthority('ADMIN') || totalCompaniesAssigned.value > FILTER_THRESHOLD)
const showList = computed(() => hotelsWithoutCurrent.value.length >= 1)

const filterClasses = computed(() => ({
  'w-full px-3 py-0.5 text-black bg-brandverylight': true,
  'outline-none border-2 border-brandlightdarker focus:border-brandmain hover:border-brandmain': true,
}))

const hotelListClasses = computed(() => ({
  'h-full': !props.scrollable,
  'max-h-[200px] overflow-y-auto': props.scrollable,
}))

const selectedHotelClasses = computed(() => ({
  'text-new-tertiary-500 font-medium': isNewUiEnabled.value && !props.light,
  'text-new-neutral-700 font-medium': isNewUiEnabled.value && props.light,
  'text-brandlight hover:text-white font-bold': !isNewUiEnabled.value && !props.light,
  'text-branddark font-bold': !isNewUiEnabled.value && props.light,
  'cursor-default': isNewUiEnabled.value && !props.light,
}))

const hotelResultClasses = computed(() => ({
  'text-new-tertiary-500 hover:text-new-neutral-0 font-normal': isNewUiEnabled.value && !props.light,
  'text-new-neutral-700 hover:text-new-neutral-800 font-normal': isNewUiEnabled.value && props.light,
  'text-brandlight hover:text-white': !isNewUiEnabled.value && !props.light,
  'text-brandmain hover:text-branddark': !isNewUiEnabled.value && props.light,
}))

const placeholder = computed(() =>
  isNewUiEnabled.value ? t('accountDropdown.searchHotelPlaceholder') : t('accountDropdown.filter')
)

onMounted(() => {
  if (globalStore.hasAuthority('ADMIN')) return
  fetchPaginatedCompanies(undefined, undefined, true)
})

const delayedOnHandleInput = delayWrapper(onHandleInput, 1000, true)

function fetchPaginatedCompanies(ids?: number[], nameLike?: string, initialFetch = false) {
  abortServicesHandler.setAbortController()
  isFetchingData.value = true
  companyService
    .fetchPaginated(0, PAGE_SIZE, SORT_BY_KEY, ASCENDING, ids, nameLike)
    .then((response) => {
      fetchedCompanies.value = response.content

      if (initialFetch) {
        totalCompaniesAssigned.value = response.totalElements
      }
    })
    .catch(silenceAbortError)
    .finally(() => (isFetchingData.value = false))
}

function onHandleInput() {
  abortServicesHandler.abort()
  if (IS_DIGIT_REGEX.test(hotelFilter.value)) {
    fetchPaginatedCompanies([parseInt(hotelFilter.value)])
  } else {
    fetchPaginatedCompanies(undefined, hotelFilter.value.trim() || undefined)
  }
}

async function switchHotelId(company: Company) {
  CompanyPropertiesCookie.set(company)
  await companyApi.updateLastUsedDate(company.id)
  window.location.replace('/')
}
</script>

<template>
  <div class="w-full flex flex-col" :class="isNewUiEnabled ? 'gap-y-3' : 'gap-y-4'">
    <div class="flex flex-col gap-y-4">
      <div v-if="showFilter">
        <TextInput v-if="isNewUiEnabled" v-model="hotelFilter" :placeholder="placeholder" @value-changed="delayedOnHandleInput" />
        <input v-else v-model="hotelFilter" :class="filterClasses" :placeholder="placeholder" @input="delayedOnHandleInput" />
      </div>

      <span :class="selectedHotelClasses">{{
        globalStore.hasAuthority('ADMIN') ? `${companyStore.name} (${companyStore.id})` : companyStore.name
      }}</span>
    </div>

    <div v-if="isFetchingData">
      <span>{{ `${$t('accountDropdown.loading')} ...` }}</span>
    </div>
    <div v-else-if="showList" class="flex flex-col" :class="[isNewUiEnabled ? 'gap-y-3' : 'gap-y-2', hotelListClasses]">
      <div v-for="hotel in hotelsWithoutCurrent" :key="hotel.id" :class="hotelResultClasses">
        <button class="text-left" type="button" @click="switchHotelId(hotel)">
          {{ globalStore.hasAuthority('ADMIN') ? `${hotel.name} (${hotel.id})` : hotel.name }}
        </button>
      </div>
    </div>
  </div>
</template>
